import { AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react';
import { useMemo } from 'react';
import type { ReactNode } from 'react';

import { cn } from '@/utils/cn';

type AlertColor = 'success' | 'danger' | 'warning' | 'info';
type AlertSize = 'sm' | 'md';

const variantSize = {
  sm: {
    container: 'p-2',
    title: 'text-xs',
    content: 'text-xs',
    icon: 'h-4 w-4',
  },
  md: {
    container: 'p-4',
    title: 'text-sm',
    content: 'text-sm',
    icon: 'h-5 w-5',
  },
};

type Props = {
  className?: string;
  variant?: AlertColor;
  size?: AlertSize;
  children: ReactNode;
  actions?: ReactNode;
  hideIcon?: boolean;
};

const Alert = ({
  className = '',
  variant = 'info',
  size = 'md',
  children,
  actions,
  hideIcon = false,
}: Props) => {
  const variantStyles = {
    warning: {
      container: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
      title: 'text-yellow-800',
      icon: <AlertTriangle className="h-5 w-5 text-yellow-400" aria-hidden="true" />,
    },
    danger: {
      container: 'bg-rose-50 text-rose-700 border border-rose-200',
      title: 'text-rose-800',
      icon: <XCircle className="h-5 w-5 text-rose-400" aria-hidden="true" />,
    },
    success: {
      container: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      title: 'text-emerald-800',
      icon: <CheckCircle className="h-5 w-5 text-emerald-400" aria-hidden="true" />,
    },
    info: {
      container: 'bg-blue-50 text-blue-700 border border-blue-200',
      title: 'text-blue-800',
      icon: <Info className="h-5 w-5 text-blue-400" aria-hidden="true" />,
    },
  };

  const { container, icon } = variantStyles[variant];

  const styleSize = useMemo(() => {
    return variantSize[size];
  }, [size]);

  return (
    <div className={cn(`rounded-md p-4 ${container}`, styleSize.container, className)}>
      <div className="flex items-center">
        {!hideIcon ? (
          <div className={cn('mr-2 shrink-0 self-start', styleSize.icon)}>{icon}</div>
        ) : null}

        <div className={cn(styleSize.content)}>{children}</div>

        {actions && <div className="ml-auto">{actions}</div>}
      </div>
    </div>
  );
};

export default Alert;
